import { useEffect, useState } from "react";
import { api, formatInt, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Link } from "react-router-dom";

export default function Returns() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/returns")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !data) return <div className="text-[color:var(--bl-text-dim)]">Loading returns…</div>;

    const totalQty = (data.by_reason || []).reduce((a, b) => a + (b.qty || 0), 0);
    const chartTip = { background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" };

    return (
        <div>
            <PageHeader title="Returns" subtitle={`All-time · ${data.count} return events`} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <KpiCard label="Total returns (qty)" value={formatInt(totalQty)} testid="kpi-returns-total" />
                <KpiCard label="Top reason" value={(data.by_reason[0]?.reason || "—").replace(/_/g, " ")} sub={`${formatInt(data.by_reason[0]?.qty || 0)} units`} accent="orange" testid="kpi-returns-top-reason" />
                <KpiCard label="Top region" value={data.by_region[0]?.region || "—"} sub={`${formatInt(data.by_region[0]?.qty || 0)} units`} accent="orange" testid="kpi-returns-top-region" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="returns-by-reason-chart">
                    <div className="font-serif-display text-xl mb-3">By reason</div>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.by_reason}>
                                <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                <XAxis dataKey="reason" stroke="#75614f" fontSize={10} tickFormatter={(r) => r.replace(/_/g, " ").slice(0, 14)} />
                                <YAxis stroke="#75614f" fontSize={10} />
                                <Tooltip contentStyle={chartTip} />
                                <Bar dataKey="qty" fill="#e35a00" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="returns-by-region-chart">
                    <div className="font-serif-display text-xl mb-3">By region</div>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.by_region}>
                                <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                <XAxis dataKey="region" stroke="#75614f" fontSize={10} />
                                <YAxis stroke="#75614f" fontSize={10} />
                                <Tooltip contentStyle={chartTip} />
                                <Bar dataKey="qty" fill="#0079c2" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm" data-testid="returns-by-sku">
                    <div className="px-5 py-3 border-b border-[color:var(--bl-border)] font-serif-display text-xl">Returns by product</div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                            <tr><th className="text-left px-4 py-2">Product</th><th className="text-right px-4 py-2">Qty returned</th></tr>
                        </thead>
                        <tbody>
                            {data.by_sku.map((r) => (
                                <tr key={r.sku} className="border-b border-[color:var(--bl-border)]">
                                    <td className="px-4 py-2">
                                        <Link to={`/products/${encodeURIComponent(r.sku)}`} className="hover:underline">
                                            <div className="line-clamp-1">{r.display_name}</div>
                                            <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)]">{r.sku}</div>
                                        </Link>
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono-num">{r.qty}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-x-auto shadow-sm">
                    <div className="px-5 py-3 border-b border-[color:var(--bl-border)] font-serif-display text-xl">Recent returns</div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                            <tr>
                                <th className="text-left px-4 py-2">Date</th>
                                <th className="text-left px-3 py-2">RRN</th>
                                <th className="text-left px-3 py-2">Product</th>
                                <th className="text-right px-3 py-2">Qty</th>
                                <th className="text-left px-3 py-2">Reason</th>
                            </tr>
                        </thead>
                        <tbody data-testid="returns-table">
                            {data.items.slice(0, 50).map((r) => (
                                <tr key={r.seller_return_id} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                    <td className="px-4 py-2 font-mono-num text-xs">{r.return_date}</td>
                                    <td className="px-3 py-2 font-mono-num text-xs">{r.return_reference_number}</td>
                                    <td className="px-3 py-2">
                                        <Link to={`/products/${encodeURIComponent(r.sku)}`} className="hover:underline line-clamp-1">{r.display_name || r.sku}</Link>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num">{formatInt(r.quantity)}</td>
                                    <td className="px-3 py-2 text-xs">{(r.return_reason || "").replace(/_/g, " ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
